import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useApplicationSettings from "../../../../Store/ApplicationSettings";
import styles from "./GiveawayDetails.module.css";
import {
  claimGiveaway,
  getFullGiveaway,
  purchaseCoupons,
  getInvoice,
} from "../../../../Services/Requests";
import { Loader } from "../../../../Components/Loader/Loader";
import useUserStore from "../../../../Store/UserStore";
import GiveawayModal from "../GiveawayModal/GiveawayModal";
import { CouponType } from "../GiveawayModal/GiveawayModal";
import { useModalStore } from "../../../../Store/ModalStore";
import { useNotificationStore } from "../../../../Store/NotificationStore";
import GiveawayBanner from "../GiveawayBanner/GiveawayBanner";
import { GiveawayInfo } from "../GiveawayInfo/GiveawayInfo";
import { GiveawayPrizes } from "../GiveawayPrizes/GiveawayPrizes";
import {
  GiveawayActive,
  GiveawayArchieve,
  FormattedPrizeRange,
  Prize,
  EnterPriceItem,
} from "../../types";

function getFormattedPrizeRanges(prizes: Prize[]): FormattedPrizeRange[] {
  return prizes.map((prize) => ({
    place:
      prize.range_start === prize.range_end
        ? prize.range_start
        : `${prize.range_start}-${prize.range_end}`,
    type: prize.type,
    amount: prize.amount,
    urls: prize.urls,
    name: prize.name,
    number: prize.number,
  }));
}

const GiveawayDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const tg = window.Telegram.WebApp;
  const userID = tg.initDataUnsafe.user.id;
  const [giveaway, setGiveaway] = useState<
    GiveawayActive | GiveawayArchieve | null
  >(null);
  const [formattedPrizes, setFormattedPrizes] = useState<FormattedPrizeRange[]>(
    []
  );
  const [activeCouponType, setActiveCouponType] = useState<CouponType | null>(
    null
  );
  const { open, setOpen } = useModalStore();
  const { setNotification } = useNotificationStore();
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [nftAnimationData, setNftAnimationData] = useState<{
    [key: string]: any;
  }>({});
  const [currentSlide, setCurrentSlide] = useState<number>(0);

  // Восстанавливаем обработчики модальных окон
  const handleOpenDCModal = () => {
    setActiveCouponType(CouponType.DC);
    setOpen(true);
  };

  const handleOpenStarModal = () => {
    setActiveCouponType(CouponType.STAR);
    setOpen(true);
  };

  useEffect(() => {
    const fetchAnimationData = async (url: string) => {
      try {
        const response = await fetch(url);

        // Проверяем тип контента
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          // Тихо игнорируем неверный тип контента
          return;
        }

        if (!response.ok) {
          // Тихо игнорируем ошибки HTTP
          return;
        }

        const data = await response.json();
        if (!data) {
          // Тихо игнорируем пустые данные
          return;
        }

        setNftAnimationData((prev) => ({ ...prev, [url]: data }));
      } catch (error) {
        // Тихо игнорируем все ошибки
        return;
      }
    };

    formattedPrizes.forEach((prize) => {
      if (prize.type === "TelegramNFT" && prize.urls?.appearance) {
        fetchAnimationData(prize.urls.appearance);
      }
    });
  }, [formattedPrizes]);

  // Обработчик покупки купонов
  const handlePurchaseCoupons = async (amount: number) => {
    if (!giveaway || !id) return;

    try {
      console.log("Покупка купонов начата", { amount, activeCouponType });
      if (activeCouponType === CouponType.DC) {
        const DCcoupons = await purchaseCoupons(Number(id), "DC", amount);
        setNotification({
          type: "Success",
          message: "Купоны были успешно куплены за DC!",
        });
        // Обновляем данные розыгрыша после покупки DC
        const response = await getFullGiveaway(Number(id));
        if (response) {
          setGiveaway(response);
          setBalanceDC(DCcoupons.new_balance);
        }
      } else if (activeCouponType === CouponType.STAR) {
        const invoice = await getInvoice({
          title: "Участие в розыгрыше",
          description: "Buy Ticket for Giveaway",
          amount: amount,
          id: Number(id),
          item_type: "raffle",
        });
        console.log("Инвойс получен", invoice);

        tg.openInvoice(invoice.invoice_link, async (status: string) => {
          console.log("Статус оплаты", status);
          if (status === "paid") {
            setNotification({
              type: "Success",
              message: "Оплата прошла успешно!",
            });
            // Обновляем данные розыгрыша только после успешной оплаты
            const response = await getFullGiveaway(Number(id));
            if (response) {
              setGiveaway(response);
            }
          } else {
            console.log("Ошибка оплаты", status);
            setNotification({
              type: "Error",
              message: "Оплата не была завершена",
            });
          }
        });
      }
    } catch (error) {
      console.error("Ошибка при покупке купонов:", error);
      setNotification({
        type: "Error",
        message: "Произошла ошибка при покупке купонов",
      });
    }
  };

  useEffect(() => {
    const fetchDetailedGiveaway = async () => {
      try {
        const response = await getFullGiveaway(Number(id));
        if (response) {
          setGiveaway(response);
          console.log(response);
          setFormattedPrizes(getFormattedPrizeRanges(response.prizes));
          console.log(formattedPrizes);
        }
      } catch (error: any) {
        console.error("Ошибка при получении розыгрыша:", error);
        // Проверяем статус ошибки
        if (
          error.response?.status === 404 ||
          error.code === "ERR_BAD_REQUEST"
        ) {
          // Добавляем небольшую задержку перед перенаправлением
          setTimeout(() => {
            navigate("/giveaway", { replace: true });
          }, 100);
        }
      }
    };
    fetchDetailedGiveaway();
  }, [id, navigate]);

  // useEffect(() => {
  //   const timeout = setTimeout(() => {
  //     if (!giveaway) {
  //       navigate('/giveaway', { replace: true }); //TODO Выявил баг, когда розырыш просто грузит дольше обычного, и если розырыш не загрузился, то он перенаправляет на страницу розыгрышей
  //     }
  //   }, 2000);

  //   return () => clearTimeout(timeout);
  // }, [giveaway, navigate]);

  async function reRenderGiveaway() {
    const data = await getFullGiveaway(Number(id));
    setGiveaway(data);
    setFormattedPrizes(getFormattedPrizeRanges(data.prizes));
  }

  const setBalanceDC = useUserStore((state) => state.setBalanceDC);

  const { setShowMenubar } = useApplicationSettings((state) => ({
    setShowMenubar: state.setShowMenubar,
  }));

  const handleBack = useCallback(() => {
    navigate("/giveaway");
  }, [navigate]);

  const getRewardType = (
    type: string
  ): "dc" | "ticket" | "character" | "USDT" => {
    switch (type.toLowerCase()) {
      case "dc":
        return "dc";
      case "tickets":
        return "ticket";
      case "usdt":
        return "USDT";
      case "character":
        return "character";
      default:
        return "dc";
    }
  };

  function formatNumber(num: number): string {
    return num.toLocaleString("en-US");
  }

  useEffect(() => {
    setShowMenubar(false);
    tg.BackButton.show();
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
    };
  }, [handleBack, tg.BackButton, setShowMenubar]);

  async function handleClaim(place: number) {
    try {
      const response = await claimGiveaway(Number(id), place);
      console.log(response);
      if (response.type === "DC") {
        setBalanceDC(response.new_balance);
      }
      reRenderGiveaway();
      setNotification({
        type: "Success",
        message: "Награда получена",
        rewards: [
          // {
          //   type: (giveaway as GiveawayArchieve).user_metadata.prize.type,
          //   amount: (giveaway as GiveawayArchieve).user_metadata.prize.amount,
          // },
        ],
      });
    } catch (e) {
      console.log(e);
      setNotification({
        type: "Error",
        message: "Ошибка при получении награды. Попробуй позже!",
      });
    }
  }

  const openNFTDetails = (detail: string) => {
    if (detail) {
      tg.openTelegramLink(detail);
    }
  };

  if (!giveaway) {
    return <Loader />;
  }
  const handleExpired = () => {
    setTimeout(() => {
      reRenderGiveaway();
      console.log("giveaway", giveaway);
    }, 3000);
    console.log("expired");
  };

  return (
    <div className={styles.container}>
      <GiveawayBanner
        background={giveaway.background}
        mainPrizes={giveaway.main_prizes}
      />

      <GiveawayInfo
        giveaway={giveaway}
        handleExpired={handleExpired}
        handleOpenDCModal={handleOpenDCModal}
        handleOpenStarModal={handleOpenStarModal}
        handleClaim={handleClaim}
        isVisible={isVisible}
        currentSlide={currentSlide}
        setCurrentSlide={setCurrentSlide}
        userID={userID}
        tg={tg}
      />

      {/* Модальное окно для покупки купонов */}
      {open &&
        activeCouponType &&
        giveaway &&
        "enter_price" in giveaway &&
        (() => {
          const dcPrice = giveaway.enter_price.find(
            (price: EnterPriceItem) => price.type === "DC"
          )?.amount;

          const xtrPrice = giveaway.enter_price.find(
            (price: EnterPriceItem) => price.type === "XTR"
          )?.amount;

          if (activeCouponType === CouponType.DC && !dcPrice) return null;
          if (activeCouponType === CouponType.STAR && !xtrPrice) return null;

          return (
            <GiveawayModal
              couponType={activeCouponType}
              couponPrice={
                activeCouponType === CouponType.DC ? dcPrice! : xtrPrice!
              }
              onPurchase={handlePurchaseCoupons}
              purchasedDC={
                giveaway.status === "active"
                  ? (giveaway as GiveawayActive).coupons_purchased_dc
                  : 0
              }
            />
          );
        })()}

      <GiveawayPrizes
        giveaway={giveaway}
        formattedPrizes={formattedPrizes}
        userID={userID}
        openNFTDetails={openNFTDetails}
        formatNumber={formatNumber}
        getRewardType={getRewardType}
      />
    </div>
  );
};

export default GiveawayDetails;
